import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import './Carousel.css';

/**
 * Carousel — a state-driven, breakpoint-aware horizontal slider.
 *
 * Architecture (and why it's shaped this way):
 *
 *   The previous implementation leaned on native `overflow-x` scroll
 *   plus CSS scroll-snap. That gave great touch swipe feel for free,
 *   but made it impossible to control the *animation curve* of arrow
 *   clicks — the browser owns the scroll engine. Every attempt to
 *   layer a custom rAF tween on top fought scroll-snap and looked
 *   instantaneous.
 *
 *   This component instead drives motion through React state:
 *
 *     index            — leftmost visible slide (discrete, 0..maxIndex)
 *     dragOffsetPx     — px finger displacement during an active swipe
 *     isDragging       — true while the pointer is down on the viewport
 *
 *   The track is positioned by a single `transform: translateX(...)`
 *   computed from those three. ONE CSS transition rule animates the
 *   transform whenever it changes — so arrow clicks, swipe-release,
 *   and breakpoint resizes all use the same curve, the same duration,
 *   and the same code path.
 *
 *   Swipe gestures use Pointer Events (touch + mouse + pen, one API).
 *   On release we decide whether to advance based on EITHER distance
 *   (>= SWIPE_RATIO of the viewport) OR final velocity (a fast flick).
 *
 * Public API:
 *
 *     <Carousel
 *       items={ARRAY}
 *       renderItem={(item, i) => <Card item={item} />}
 *       visibleCount={{ base: 1, md: 2, lg: 3 }}
 *       gap={24}
 *     />
 *
 *   Items can mark their primary visual with `[data-arrow-anchor]`;
 *   arrow buttons then vertically centre on that element rather than
 *   the geometric centre of the whole slide.
 */

const BREAKPOINTS = { base: 0, sm: 640, md: 769, lg: 1024, xl: 1280 };

// Gesture decision thresholds. Documented at the top so the feel is
// adjustable in one place.
const SWIPE_RATIO = 0.18;       // fraction of viewport that counts as "intentional"
const SWIPE_VELOCITY = 0.45;    // px/ms — a fast flick advances even on short drag
const RUBBER_BAND = 0.3;        // resistance factor when dragging past bounds
// Distance the pointer must travel before we commit to "this is a drag".
// Below this threshold, pointerdown / pointerup is treated as a plain
// click and the native click event is allowed to reach child elements
// (e.g. <Link>). Above it, we capture the pointer and run the swipe
// gesture. Tuned slightly above Chrome's native click-vs-drag heuristic
// so accidental jitter during a click never trips the swipe handler.
const DRAG_THRESHOLD = 8;       // px

// The pre-renderer captures every page at this viewport width (see
// scripts/prerender.mjs). The first client render has to produce the same
// DOM whatever the visitor's real width is - arrows and dots depend on
// `visible` - or hydration fails and the whole page is rebuilt on a phone.
// So `visible` starts from this width and the resize effect corrects it,
// with a re-render, as soon as the component mounts.
const PRERENDER_VIEWPORT_WIDTH = 1280;

function resolveVisible(visibleCount, width) {
  if (typeof visibleCount === 'number') return visibleCount;
  if (typeof window === 'undefined' && width == null) return visibleCount.base ?? 1;
  const w = width ?? window.innerWidth;
  for (const key of ['xl', 'lg', 'md', 'sm', 'base']) {
    if (w >= BREAKPOINTS[key] && visibleCount[key] != null) {
      return visibleCount[key];
    }
  }
  return visibleCount.base ?? 1;
}

export default function Carousel({
  items = [],
  renderItem,
  visibleCount = 1,
  gap = 16,
  ariaLabel = 'Carousel',
  className = '',
  itemClassName = '',
  // When true, renders a row of pagination dots (one per item) above the
  // cards. Every dot whose card is currently on screen is highlighted, so
  // with 3 visible of 5 you see 3 lit dots that shift as you page.
  dots = false,
  // Optional controlled mode. Pass `index` (plus `onIndexChange`) to drive the
  // carousel from outside - the product card's thumbnail strip needs to both
  // set the current slide and reflect swipes back, which internal-only state
  // cannot express. Omit both and the carousel manages its own index exactly
  // as before.
  index: controlledIndex,
  onIndexChange,
  // Arrows are shown automatically when there is more to scroll to. Set false
  // where another control (thumbnails) already does the navigating.
  arrows = true,
}) {
  const [uncontrolledIndex, setUncontrolledIndex] = useState(0);
  const isControlled = controlledIndex != null;
  const index = isControlled ? controlledIndex : uncontrolledIndex;
  const setIndex = useCallback((updater) => {
    if (isControlled) {
      const next = typeof updater === 'function' ? updater(controlledIndex) : updater;
      if (next !== controlledIndex && onIndexChange) onIndexChange(next);
      return;
    }
    setUncontrolledIndex(updater);
  }, [isControlled, controlledIndex, onIndexChange]);
  const [visible, setVisible] = useState(() => resolveVisible(visibleCount, PRERENDER_VIEWPORT_WIDTH));
  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const rootRef = useRef(null);
  const viewportRef = useRef(null);
  // Drag bookkeeping kept in a ref so we don't re-render on every pointermove.
  // Two-phase model:
  //   pending — pointer is down, but we haven't decided drag vs click yet.
  //             No pointer capture, no isDragging state. Native click can
  //             still fire on child <Link> elements.
  //   active  — movement exceeded DRAG_THRESHOLD; now we capture the
  //             pointer and own the gesture. Native click is suppressed
  //             because the cursor moved past Chrome's click tolerance.
  const dragRef = useRef({ pending: false, active: false, startX: 0, startTime: 0, pointerId: null });

  // -- Breakpoint-driven visible count ----------------------------------
  useEffect(() => {
    if (typeof visibleCount === 'number') {
      setVisible(visibleCount);
      return undefined;
    }
    const update = () => setVisible(resolveVisible(visibleCount));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [visibleCount]);

  const maxIndex = Math.max(0, items.length - visible);

  // Clamp index back into range when breakpoint changes shrink the view.
  useEffect(() => {
    if (isControlled) return;
    setUncontrolledIndex((i) => (i > maxIndex ? maxIndex : i));
  }, [maxIndex, isControlled]);

  const goTo = useCallback((nextIndex) => {
    setIndex((i) => {
      const clamped = Math.max(0, Math.min(maxIndex, nextIndex));
      return clamped === i ? i : clamped;
    });
  }, [maxIndex]);

  const prev = useCallback(() => goTo(index - 1), [goTo, index]);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);

  // -- Transform math ---------------------------------------------------
  // One discrete step shifts the track by (100% + gap) / visible. The
  // drag offset is layered on as raw pixels so the track follows the
  // finger 1:1 during gestures.
  // The transition itself lives in Carousel.css (.crsl-track) so it can be
  // exempted from the global reduced-motion kill; during an active drag the
  // .crsl-track--dragging class removes it so the track follows the finger.
  const trackStyle = {
    transform: `translateX(calc(${-index} * (100% + ${gap}px) / ${visible} + ${dragOffsetPx}px))`,
    gap: `${gap}px`,
  };

  const slideStyle = {
    flex: `0 0 calc((100% - ${(visible - 1) * gap}px) / ${visible})`,
  };

  // -- Pointer-based swipe ---------------------------------------------
  // The handlers below implement the two-phase pending/active model
  // documented on dragRef above. The reason for the indirection is that
  // calling setPointerCapture() unconditionally in onPointerDown causes
  // ALL subsequent click events to fire on the viewport instead of on
  // descendant elements (e.g. a <Link> inside a slide), so plain mouse
  // clicks on cards never navigated. By deferring capture until the
  // pointer has actually travelled DRAG_THRESHOLD pixels, a tap-and-
  // release behaves like a normal click and the native click event
  // reaches the link.
  const onPointerDown = (e) => {
    if (e.target.closest('.crsl-arrow')) return;             // arrows handled separately
    if (e.pointerType === 'mouse' && e.button !== 0) return; // primary button only
    dragRef.current = {
      pending: true,   // may or may not become a drag — decided in pointermove
      active: false,
      startX: e.clientX,
      startTime: e.timeStamp,
      pointerId: e.pointerId,
    };
    // Intentionally do NOT call setIsDragging(true) or setPointerCapture
    // here — both would suppress the click event on child links.
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag.pending && !drag.active) return;
    if (e.pointerId !== drag.pointerId) return;
    const dx = e.clientX - drag.startX;

    // Promote pending → active once the pointer has crossed the
    // drag threshold. Until then, ignore movement so a wobbly click
    // doesn't shift the track.
    if (drag.pending) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      drag.pending = false;
      drag.active = true;
      setIsDragging(true);
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    }

    const atStart = index === 0 && dx > 0;
    const atEnd = index === maxIndex && dx < 0;
    setDragOffsetPx(atStart || atEnd ? dx * RUBBER_BAND : dx);
  };

  const finishDrag = (e) => {
    const drag = dragRef.current;
    if (!drag.pending && !drag.active) return;
    if (e && e.pointerId !== drag.pointerId) return;

    if (drag.active) {
      // We owned the gesture — decide whether to advance the carousel.
      const dx = e ? e.clientX - drag.startX : 0;
      const dt = e ? Math.max(1, e.timeStamp - drag.startTime) : 1;
      const velocity = Math.abs(dx) / dt;
      const vp = viewportRef.current;
      const viewportW = vp ? vp.clientWidth : 1;
      const passedDistance = Math.abs(dx) / viewportW > SWIPE_RATIO;
      const passedFlick = velocity > SWIPE_VELOCITY && Math.abs(dx) > 20;

      if (passedDistance || passedFlick) {
        goTo(index + (dx < 0 ? 1 : -1));
      }
      // Release: clear drag state. Setting dragOffsetPx → 0 with
      // isDragging → false triggers the transition automatically, so the
      // track settles into its new index with the same easing as an
      // arrow click.
      setIsDragging(false);
      setDragOffsetPx(0);
      try { if (e) e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    }
    // If we were only pending (never crossed the threshold), there is
    // nothing to undo — no capture, no isDragging, no offset. The
    // browser's native click event continues to descendants unimpeded.
    dragRef.current = { pending: false, active: false, startX: 0, startTime: 0, pointerId: null };
  };

  // -- Arrow-anchor measurement ----------------------------------------
  // Position the arrow buttons vertically so they sit at the centre of
  // the slide's primary visual rather than the geometric centre of the
  // slide (which on image-on-top cards drifts down into the caption).
  useLayoutEffect(() => {
    const root = rootRef.current;
    const vp = viewportRef.current;
    if (!root || !vp || !items.length) return undefined;

    const measure = () => {
      const slide = vp.querySelector('.crsl-slide');
      if (!slide) return;
      const anchor = slide.querySelector('[data-arrow-anchor]') || slide.querySelector('img');
      if (!anchor) {
        root.style.removeProperty('--crsl-arrow-top');
        return;
      }
      const slideRect = slide.getBoundingClientRect();
      const anchorRect = anchor.getBoundingClientRect();
      if (!slideRect.height) return;
      const centerPct = ((anchorRect.top + anchorRect.height / 2) - slideRect.top) / slideRect.height * 100;
      root.style.setProperty('--crsl-arrow-top', `${centerPct.toFixed(2)}%`);
    };

    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      const slide = vp.querySelector('.crsl-slide');
      if (slide) ro.observe(slide);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [items.length, visible]);

  const showArrows = arrows && items.length > visible;

  return (
    <div
      ref={rootRef}
      className={`crsl ${className}`.trim()}
      role="region"
      aria-label={ariaLabel}
      aria-roledescription="carousel"
    >
      {dots && showArrows && (
        <div className="crsl-dots" role="tablist" aria-label="Slide navigation">
          {items.map((_, i) => {
            const onScreen = i >= index && i < index + visible;
            return (
              <button
                key={i}
                type="button"
                className={`crsl-dot${onScreen ? ' crsl-dot--active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1} of ${items.length}`}
                aria-current={onScreen ? 'true' : undefined}
              />
            );
          })}
        </div>
      )}
      <div
        className="crsl-viewport"
        ref={viewportRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
      >
        <div className={`crsl-track${isDragging ? ' crsl-track--dragging' : ''}`} style={trackStyle}>
          {items.map((it, i) => {
            const isVisible = i >= index && i < index + visible;
            return (
              <div
                key={i}
                className={`crsl-slide ${itemClassName}`.trim()}
                style={slideStyle}
                aria-hidden={!isVisible}
                aria-roledescription="slide"
                aria-label={`${i + 1} of ${items.length}`}
              >
                {renderItem(it, i)}
              </div>
            );
          })}
        </div>
      </div>
      {showArrows && (
        <>
          <button
            type="button"
            className="crsl-arrow crsl-arrow--prev"
            onClick={prev}
            disabled={index === 0}
            aria-label="Previous slide"
          >‹</button>
          <button
            type="button"
            className="crsl-arrow crsl-arrow--next"
            onClick={next}
            disabled={index >= maxIndex}
            aria-label="Next slide"
          >›</button>
        </>
      )}
    </div>
  );
}
